import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import { useTextHighlight } from '../utils/highlightUtils';

const Class11SentencePractice02 = () => {
  const navigate = useNavigate();

  const highlightRef = useRef(null);
  useTextHighlight(highlightRef);

  const [showHelp, setShowHelp] = useState(false);

  const questionRefs = useRef([]);
  const rightColRef = useRef(null);

  // Make sure your answers state exists
  const [answers, setAnswers] = useState(Array(20).fill(''));
  const [currentQuestion, setCurrentQuestion] = useState(14);
  const onNext = () => {
    setCurrentQuestion(prev => Math.min(prev + 1, 19));
  };

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(100 * 60); // 30 minutes in seconds
  const [showSeconds, setShowSeconds] = useState(false);

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          navigate('/');  // Redirect to home
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  // Format time function
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (showSeconds) {
      return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
    }
    return `${mins} minutes remaining`;
  };

  const handleStartTest = () => {
    navigate(`/guided-practice-01-form`);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (e) => {
    if (!audioRef.current) return;
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    audioRef.current.volume = vol;
    setIsMuted(vol === 0);
  };

  const handleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.muted = false;
      setIsMuted(false);
      audioRef.current.volume = volume;
    } else {
      audioRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const formatAudioTime = (t) => {
    if (isNaN(t)) return '00:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const GreenCheck = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
    >
      <path
        d="M7 11L9 13.5L16 7"
        stroke="#7DD77D"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );


  return (
    <div className="min-h-screen bg-white flex flex-col font-[Arial]">
      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />

      {/* Top Bar (fixed) */}
      <div className="sticky top-0 z-30 bg-white">
        <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
          {/* Left + Center combined on left side */}
          <div className="flex flex-col justify-center space-y-0.5 pl-6">
            <span className="text-[14px] text-left text-black font-bold leading-tight">
              HEXA'S Education
            </span>
            <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
              <span
                onMouseEnter={() => setShowSeconds(true)}
                onMouseLeave={() => setShowSeconds(false)}
                className="cursor-pointer"
              >
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>

          {/* Right: Icons and Finish Button */}
          <div className="flex items-center space-x-5">
            <button
              onClick={() => navigate('/')}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Back Home
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Finish test
            </button>
            <div className="flex items-center space-x-8 pl-2 pr-2">
              {/* WiFi Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 717.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* Notification Bell Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>
              {/* Hamburger Menu Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* Pen/Edit Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        {/* Title Bar (fixed under top bar) */}
        <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
          {/* Left: Part Title */}
          <div className="flex-1 px-4 py-2 text-left">
            <h2 className="text-[23px] text-black">Part 1</h2>
            <h1 className="text-[18px] text-black">HEXA'S Reading Course Book</h1>
          </div>
          {/* Center: Absolutely Positioned Logo */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <img
              src={HEXASLogo}
              alt="HEXA'S Logo"
              className="h-[50px] object-contain"
            />
          </div>
          {/* Right: Empty space for consistency */}
          <div className="flex items-center justify-end w-[300px] pr-4">
            {/* Empty space - no audio player for reading */}
          </div>
        </div>
      </div>

      {/* Main content (scrollable) */}
      <div className="flex-1 flex flex-col items-center justify-start w-full">
        {/* Two-Column Main Content Area */}
        <div ref={highlightRef}
          className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
          style={{ height: 'calc(100vh - 180px)' }}
        >
          {/* Left: Reading Passage */}
          <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
            <div className="mb-8">
              <h2 className="text-[16px] font-bold mb-4 text-center">Forest management in Pennsylvania, USA</h2>
              <h3 className="text-[14px] font-semibold mb-6 text-center italic">
                How managing low-quality wood (also known as low-use wood) for bioenergy can encourage sustainable forest management
              </h3>

              {/* Remaining paragraphs - left aligned */}
              <div className="text-[14px] space-y-4 text-left">
                <p>
                  <strong>A</strong> A tree’s ‘value’ depends on several factors including its species, size, form, condition, quality, function, and accessibility, and depends on the management goals for a given forest. The same tree can be valued very differently by each person who looks at it. A large, straight black cherry tree has high value as timber to be cut into logs or made into furniture, but for a landowner more interested in wildlife habitat, the real value of that stem (or trunk) may be the food it provides to animals. Likewise, if the tree suffers from black knot disease, its value for timber decreases, but to a woodworker interested in making bowls, it brings an opportunity for a unique and beautiful piece of art.
                </p>
                <p>
                  <strong>B</strong> In the past, Pennsylvania landowners were solely interested in the value of their trees as high-quality timber. The norm was to remove the stems of highest quality and leave behind poorly formed trees that were not as well suited to the site where they grew. This practice, called ‘high-grading’, has left a legacy of ‘low-use wood’ in the forests. Some people even call these ‘junk trees’, and they are abundant in Pennsylvania. These trees have lower economic value for traditional timber markets, compete for growth with higher-value trees, shade out desirable regeneration and decrease the health of a stand leaving it more vulnerable to poor weather and disease. Management that specifically targets low-use wood can help landowners manage these forest health issues, and wood energy markets help promote this.
                </p>
                <p>
                  <strong>C</strong> Wood energy markets can accept less expensive wood material of lower quality than would be suitable for traditional timber markets. Most wood used for energy in Pennsylvania is used to produce heat or electricity through combustion. Many schools and hospitals use wood boiler systems to heat and power their facilities, many homes are primarily heated with wood, and some coal plants incorporate wood into their coal streams to produce electricity. Wood can also be gasified for electrical generation and can even be made into liquid fuels like ethanol and gasoline for lorries and cars. All these products are made primarily from low-use wood. Several tree- and plant-cutting approaches, which could greatly improve the long-term quality of a forest, focus strongly or solely on the use of wood for those markets.
                </p>
                <p>
                  <strong>D</strong> One such approach is called a Timber Stand Improvement (TSI) Cut. In a TSI Cut, really poor-quality tree and plant material is cut down to allow more space, light, and other resources to the highest-valued stems that remain. Removing invasive plants might be another primary goal of a TSI Cut. The stems that are left behind might then grow in size and develop more foliage and larger crowns or tops that produce more coverage for wildlife; they have a better chance to regenerate in a less crowded environment. TSI Cuts can be tailored to one farmer’s specific management goals for his or her land.
                </p>
                <p>
                  <strong>E</strong> Another approach that might yield a high amount of low-use wood is a Salvage Cut. With the many pests and pathogens visiting forests including hemlock wooly adelgid, Asian longhorned beetle, emerald ash borer, and gypsy moth, to name just a few, it is important to remember that those working in the forests can help ease these issues through cutting procedures. These types of cut reduce the number of sick trees and seek to manage the future spread of a pest problem. They leave vigorous trees that have stayed healthy enough to survive the outbreak.
                </p>
                <p>
                  <strong>F</strong> A Shelterwood Cut, which only takes place in a mature forest that has already been thinned several times, involves removing all the mature trees when other seedlings have become established. This then allows the forester to decide which tree species are regenerated. It leaves a young forest where all trees are at a similar point in their growth. It can also be used to develop a two-tier forest so that there are two harvests and the money that comes in is spread out over a decade or more.
                </p>
                <p>
                  <strong>G</strong> Thinnings and dense and dead wood removal for fire prevention also center on the production of low-use wood. However, it is important to remember that some retention of what many would classify as low-use wood is very important. The tops of trees that have been cut down should be left on the site so that their nutrients cycle back into the soil. In addition, trees with many cavities are extremely important habitats for insect predators like woodpeckers, bats and small mammals. They help control problem insects and increase the health and resilience of the forest. It is also important to remember that not all small trees are low-use. For example, many species like hawthorn provide food for wildlife. Finally, rare species of trees in a forest should also stay behind as they add to its structural diversity.
                </p>
              </div>
            </div>
          </div>





          {/* Right: Questions */}
          <div
            className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto relative"
            style={{ overflowAnchor: 'none' }} // prevents scroll jumping
          >
            {/* Questions Header */}
            <div className="mb-6 w-full">
              <h3 className="text-[15px] font-bold mb-3 text-left">
                Questions 22 – 26
              </h3>
              <p className="text-[14px] mb-4 text-left">
                Complete the sentences below.<br />
                Choose <strong>ONE WORD ONLY</strong> from the passage for each answer.
              </p>
            </div>

            {/* Questions List */}
            <div className="space-y-6 w-full text-[14px] font-medium text-black text-left">
              {/* Question 22 */}
              <div className="break-words whitespace-normal">
                <strong>22.</strong>&nbsp; Some dead wood is removed to avoid the possibility of&nbsp;
                <input
                  ref={(el) => (questionRefs.current[21] = el)}
                  type="text"
                  placeholder="22"
                  className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[140px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                  value={answers[21] || ""}
                  onChange={(e) => {
                    const newAnswers = [...answers];
                    newAnswers[21] = e.target.value;
                    setAnswers(newAnswers);
                  }}
                />.
              </div>

              {/* Question 23 */}
              <div className="break-words whitespace-normal">
                <strong>23.</strong>&nbsp; The&nbsp;
                <input
                  ref={(el) => (questionRefs.current[22] = el)}
                  type="text"
                  placeholder="23"
                  className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[140px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                  value={answers[22] || ""}
                  onChange={(e) => {
                    const newAnswers = [...answers];
                    newAnswers[22] = e.target.value;
                    setAnswers(newAnswers);
                  }}
                /> from the tops of cut trees can help improve soil quality.
              </div>

              {/* Question 24 */}
              <div className="break-words whitespace-normal">
                <strong>24.</strong>&nbsp; Some damaged trees should be left, as their&nbsp;
                <input
                  ref={(el) => (questionRefs.current[23] = el)}
                  type="text"
                  placeholder="24"
                  className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[140px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                  value={answers[23] || ""}
                  onChange={(e) => {
                    const newAnswers = [...answers];
                    newAnswers[23] = e.target.value;
                    setAnswers(newAnswers);
                  }}
                /> provide habitats for a range of creatures.
              </div>

              {/* Question 25 */}
              <div className="break-words whitespace-normal">
                <strong>25.</strong>&nbsp; Some trees that are small, such as&nbsp;
                <input
                  ref={(el) => (questionRefs.current[24] = el)}
                  type="text"
                  placeholder="25"
                  className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[140px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                  value={answers[24] || ""}
                  onChange={(e) => {
                    const newAnswers = [...answers];
                    newAnswers[24] = e.target.value;
                    setAnswers(newAnswers);
                  }}
                />, are a source of food for animals and insects.
              </div>

              {/* Question 26 */}
              <div className="break-words whitespace-normal">
                <strong>26.</strong>&nbsp; Any trees that are&nbsp;
                <input
                  ref={(el) => (questionRefs.current[25] = el)}
                  type="text"
                  placeholder="26"
                  className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[140px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                  value={answers[25] || ""}
                  onChange={(e) => {
                    const newAnswers = [...answers];
                    newAnswers[25] = e.target.value;
                    setAnswers(newAnswers);
                  }}
                /> should be left to grow, as they add to the variety of species in the forest.
              </div>
            </div>
          </div>








        </div>
      </div>

      {/* Bottom Navigation (fixed footer) */}
      <div className="sticky bottom-0 left-0 w-full bg-white border-t border-gray-300 z-30 flex items-center justify-start px-4 py-3">
        {/* New Navigation Bar */}
        <div className="flex items-center max-w-2xl">
          {/* Part Label with bar above */}
          <div className="flex flex-col items-center mr-4">
            <div className="h-1 w-12 mb-1 rounded-full bg-gray-300" />
            <span className="text-[15px] font-bold text-gray-700">Part 2</span>
          </div>

          {/* Numbers with lines above */}
          <div className="flex items-end space-x-2">
            {[22, 23, 24, 25, 26].map((num) => {
              const isActive = currentQuestion === num;
              const isAnswered = answers[num - 1] && answers[num - 1] !== '';
              return (
                <div key={num} className="flex flex-col items-center">
                  {/* Line above number */}
                  <div
                    className="h-1 w-7 mb-1 rounded-full"
                    style={{ backgroundColor: isAnswered ? '#C9A842' : '#D1D5DB' }}
                  ></div>

                  {/* Number button */}
                  <button
                    onClick={() => {
                      setCurrentQuestion(num);
                      setTimeout(() => {
                        const input = questionRefs.current[num - 1];
                        if (input) {
                          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          input.focus();
                        }
                      }, 50); // tiny delay for DOM update
                    }}
                    className={`w-7 h-7 flex items-center justify-center text-[15px] font-bold
                ${isActive ? 'border-2 border-sky-400 bg-white text-sky-700 rounded-md' : 'text-gray-700'}
                transition-colors duration-150`}
                    style={{ boxShadow: isActive ? '0 0 0 0px #38bdf8 inset' : 'none' }}
                  >
                    {num}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>







    </div>
  );
};

export default Class11SentencePractice02;
